import React, { useState, FC, useRef, useEffect } from "react";
import {
  levelOneInstruction,
  levelTwoInstruction,
  levelThreeInstructions,
} from "../helpers/constants";
import axios from "axios";
import Typewriter from "typewriter-effect";
import { AIResponseComponent } from "./AIResponseComponent";

export type ChallengeProps = {
  level: number;
  updateLevel: (newLevel: number) => void;
};

const InstructionMap: Record<number, string> = {
  1: levelOneInstruction,
  2: levelTwoInstruction,
  3: levelThreeInstructions,
};

export const Challenge: FC<ChallengeProps> = ({ level, updateLevel }) => {
  const [inputText, setInputText] = useState("");
  const [AIResponse, setAIResponse] = useState("");
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [password, setPassword] = useState("");

  const [moveOn, setMoveOn] = useState<boolean>(false);
  const formRef = useRef(null);
  const getAiResponse = async () => {
    setLoading(true);
    setAIResponse("");
    try {
      const response = await axios.post(
        process.env.REACT_APP_API_URL + "/level" + level,
        {
          data: inputText,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );
      const { data } = response;
      // Level 1 check to see if user passes--------------
      if (level === 1) {
        const passed: boolean = data?.passed;

        if (passed) {
          alert("🎉Great Job. You passed the first level!🎉");
          setMoveOn(true);
        }
      }
      setAIResponse(data["response"]);
    } catch (error: any) {
      // rate limiting check here
      console.log(error);
      if (error?.response?.status === 429) {
        alert(
          `Thank you for playing our game! Please take a short break. You should be good to continue in approximately half an hour.`
        );
      } else {
        // an error is thrown for some reason
        setAIResponse("I am sorry, I cannot help you with that");
      }
      // console.error("There was an error!", error);
    }
    setLoading(false);
  };

  const checkPassword = async () => {
    setPasswordLoading(true);
    try {
      // dont need to use the token here.
      const response = await axios.post(
        process.env.REACT_APP_API_URL + "/checkPassword",
        {
          data: password,
        }
      );
      const { data } = response;
      if (data.passed) {
        alert("🎉Great Job. You passed the second level!🎉");
        updateLevel(3);
      } else {
        alert("Try again, that was the wrong password!");
      }
    } catch (error) {
      console.error("There was an error!", error);
    }
    setPasswordLoading(false);
  };

  const handleKeyDown = (e: any) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault(); // Prevent the default behavior (newline)
      if (formRef.current) {
        (formRef.current as any)?.requestSubmit(); // Programmatically submit the form
      }
    }
  };

  return (
    <div className="flex flex-col items-center justify-center p-10">
      <div className="lg:px-20 md:px-12 sm:px-4 absolute max-w-screen text-center top-24">
        <div className="mb-20">
          <h1 className="text-2xl mb-6">Level {level}</h1>
          <h1 className="lg:text-xl text-white font-bold mx-4 type-level1 md:text-sm">
            <Typewriter
              key={level}
              onInit={(typewriter) => {
                typewriter
                  .changeDelay(25)
                  .typeString(InstructionMap[level])
                  .start();
              }}
            />
          </h1>
        </div>

        {/* AI RESPONSE HERE */}
        <div className="w-full h-64 overflow-auto white-scrollbar">
          <AIResponseComponent aiResponse={AIResponse} />
        </div>
      </div>

      {/* INPUT BOX BELOW */}
      <div className="absolute lg:bottom-20 md:bottom-20 bottom-12 w-full lg:px-20 px-8">
        <form
          className="flex space-x-4 items-center justify-center w-full mx-auto"
          onSubmit={(e) => {
            e.preventDefault();
            getAiResponse();
          }}
          ref={formRef}
        >
          <textarea
            className={`min-h-10 h-10 p-2  w-full ${
              loading
                ? "border-gray-600 text-gray-600"
                : "border-hackerGreen focus:border-hackerGreen"
            } bg-black border  rounded-md focus:outline-none max-h-52`}
            value={inputText}
            disabled={loading}
            onChange={(e) => {
              setInputText(e.target.value);
            }}
            onKeyDown={handleKeyDown}
          />
          <button
            type="submit"
            disabled={loading}
            className={`border ${
              loading
                ? "border-gray-600 text-gray-600 "
                : "hover:text-black hover:bg-hackerGreen border-hackerGreen"
            }  font-medium rounded-md text-sm px-5 py-2.5 text-center
            `}
          >
            Submit
          </button>
        </form>
        {level === 1 && moveOn && (
          <div className="pt-4 flex w-full justify-center">
            <button
              disabled={loading}
              className={`border ${
                loading
                  ? "border-gray-600 text-gray-600 "
                  : "hover:text-black hover:bg-hackerGreen border-hackerGreen"
              }  font-medium rounded-md text-sm px-5 py-2.5 text-center
          `}
              onClick={() => {
                updateLevel(2);
                setAIResponse("");
              }}
            >
              Move on
            </button>
          </div>
        )}
        {level === 2 && (
          <form
            className="flex space-x-4 items-center justify-center mx-auto mt-4 lg:w-4/12 md:w-8/12 sm:w-full"
            onSubmit={(e) => {
              e.preventDefault();
              checkPassword();
            }}
          >
            <input
              type="text"
              disabled={passwordLoading}
              className={`p-2 
              ${
                passwordLoading
                  ? "border-gray-600 text-gray-600"
                  : "border-hackerGreen focus:border-hackerGreen"
              }
              w-full bg-black border  rounded-md focus:outline-none `}
              placeholder="Enter your password"
              value={password}
              onChange={(e) => {
                setPassword(e?.target?.value);
              }}
            />
            <button
              type="submit"
              disabled={passwordLoading}
              className={`
              ${
                passwordLoading
                  ? "border-gray-600 text-gray-600"
                  : "hover:text-black hover:bg-hackerGreen border-hackerGreen"
              }
              max-h-12 border  font-medium rounded-md text-sm px-5 py-2.5 text-center`}
            >
              Check
            </button>
          </form>
        )}
      </div>
    </div>
  );
};
